/**
 * Transforms markdown so that quoted book titles inside the
 * "# Your reading path" section become Amazon-affiliate markdown links.
 *
 * Robust design:
 *  - Uses the EXACT book title list supplied by the backend (per philosopher).
 *    No regex-guessing of arbitrary quoted phrases → zero false positives
 *    (fixes over-linking of in-prose quotes like "Higher Men" or
 *    "existence precedes essence").
 *  - Only linkifies matches inside the "# Your reading path" section.
 *  - Handles straight (") and curly (“…”) quotes around a title.
 *  - Never throws; on any error returns the original markdown.
 *  - Does NOT mutate stored markdown — pure render-time transform.
 */
const AMAZON_BASE = "https://www.amazon.com/s?k=";

const NEXT_H1 = /^\s*#\s+\S/;
const READING_PATH_HEADING = /^\s*#\s+.*your\s+reading\s+path/i;

// Book list entries look like: `"Meditations" (Marcus Aurelius, Gregory Hays translation)`
// or simply: `"Thus Spoke Zarathustra"`. We only need the first quoted segment.
function extractTitle(entry) {
  const m = /"([^"]+)"|“([^”]+)”|‘([^’]+)’/.exec(entry || "");
  if (!m) return null;
  return (m[1] || m[2] || m[3] || "").trim();
}

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function amazonUrl(title, tag) {
  return `${AMAZON_BASE}${encodeURIComponent(title)}&tag=${encodeURIComponent(tag)}`;
}

export default function transformReadingPathLinks(markdown, tag, books) {
  if (!markdown || typeof markdown !== "string" || !tag) return markdown;
  const titles = (books || [])
    .map(extractTitle)
    .filter(Boolean)
    // Longest first — protects "The Analects" from being partly replaced when
    // a shorter title happens to be a substring.
    .sort((a, b) => b.length - a.length);
  if (titles.length === 0) return markdown;

  try {
    const lines = markdown.split("\n");
    let inSection = false;
    const out = lines.map((line) => {
      if (NEXT_H1.test(line)) {
        inSection = READING_PATH_HEADING.test(line);
        return line;
      }
      if (!inSection) return line;
      let mutated = line;
      for (const title of titles) {
        // Match the title surrounded by either straight or curly quotes.
        // We only replace the FIRST occurrence on the line to avoid duplicate
        // links inside a single sentence.
        const t = escapeRegExp(title);
        const re = new RegExp(`("|“|‘)${t}("|”|’)`);
        if (!re.test(mutated)) continue;
        mutated = mutated.replace(re, (_m, openQ, closeQ) => {
          const url = amazonUrl(title, tag);
          return `[${openQ}${title}${closeQ}](${url})`;
        });
      }
      return mutated;
    });
    return out.join("\n");
  } catch {
    return markdown; // never crash rendering
  }
}
